import { Avatar, AvatarFallback } from "@repo/ui";
import { Badge } from "@repo/ui";
import { Button } from "@repo/ui";
import { ScrollArea } from "@repo/ui";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@repo/ui";
import { Separator } from "@repo/ui";
import { Textarea } from "@repo/ui";
import {
  Clock,
  Forward,
  Info,
  Lock,
  Paperclip,
  Reply,
  Send,
  Star,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AVAILABLE_COLORS, COLORS_MAPPING } from "../../lib/constants";
import type { MailComponent, MailReponse, PieceJointe } from "../../lib/types";
import {
  MailStatus,
  type MailMetadata,
  type VariablesData,
} from "../../lib/types";
import type { TypeReponseMail } from "../../templates/types";
import { calculateDateWithDateRelative, getInitials, getSenderColor } from "../../lib/utils";
import { cn } from "@repo/utils";
import { useUIStore } from "../../stores/uiStore";
import { getPieceJointeContentComponent } from "../contenu-pieces-jointes";

interface MailDetailProps {
  CurrentMailComponent: MailComponent;
  mailResponse?: MailReponse;
  variablesData: VariablesData;
  isPresidentMail?: boolean;
  typeReponse?: TypeReponseMail;
  isCompleted?: boolean;
  onClose?: () => void;
  onColorChange?: (color: string) => void;
  onToggleImportant?: () => void;
  onSaveResponse?: (reponse: string, transfertA?: string) => void;
  onSaveDraft?: (reponse: string, transfertA?: string) => void;
  onStartDraft?: () => void;
  onCancelDraft?: () => void;
  onStartEdit?: () => void;
}

function InfoTooltip() {
  return (
    <div className="relative group">
      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded-full border border-muted-foreground/40 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
        aria-label="Information sur le format de réponse"
      >
        <Info className="h-3 w-3" />
      </button>
      <div className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 w-96 rounded-lg border bg-popover p-3 text-xs text-popover-foreground shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-150">
        <div className="font-medium mb-1">
          Expliquer le &ldquo;POURQUOI&rdquo; : Pour chaque action importante que vous décidez de prendre (ou de ne pas prendre), expliquez clairement pourquoi vous faites ce choix.
        </div>
        <div>Quel est votre raisonnement ? Quels facteurs avez-vous pris en compte ?</div>
      </div>
    </div>
  );
}

export function MailDetail({
  CurrentMailComponent,
  mailResponse,
  variablesData,
  isPresidentMail = false,
  typeReponse = "mail",
  isCompleted = false,
  onClose,
  onColorChange,
  onToggleImportant,
  onSaveResponse,
  onSaveDraft,
  onStartDraft,
  onCancelDraft,
  onStartEdit,
}: MailDetailProps) {
  const mailMetadata = CurrentMailComponent.metadata as MailMetadata;
  const todayDate = useUIStore((state) => state.todayDate);
  const [composeMode, setComposeMode] = useState<"reply" | "forward" | null>(
    null,
  );
  const [selectedAttachment, setSelectedAttachment] =
    useState<PieceJointe | null>(null);

  // State for compose form
  const [reponseText, setReponseText] = useState("");
  const [transferRecipients, setTransferRecipients] = useState("");

  // State for editing an existing response
  const [originalResponse, setOriginalResponse] = useState<string | null>(null);
  const [originalTransferRecipients, setOriginalTransferRecipients] = useState("");

  // Ref to track if we need to save draft
  const prevComposeModeRef = useRef<"reply" | "forward" | null>(null);
  // Ref to skip the draft-save effect when the user explicitly sends
  const isSendingRef = useRef(false);

  // Ref to track last saved response text for auto-save
  const refReponseText = useRef<string>("");

  // Refs toujours à jour pour les valeurs courantes (utilisés au démontage)
  const reponseTextRef = useRef(reponseText);
  reponseTextRef.current = reponseText;
  const transferRecipientsRef = useRef(transferRecipients);
  transferRecipientsRef.current = transferRecipients;
  const composeModeRef = useRef(composeMode);
  composeModeRef.current = composeMode;
  const onSaveDraftRef = useRef(onSaveDraft);
  onSaveDraftRef.current = onSaveDraft;

  // Sauvegarde le brouillon au démontage (ex : changement de mail sélectionné)
  useEffect(() => {
    return () => {
      if (
        !isSendingRef.current &&
        composeModeRef.current &&
        reponseTextRef.current.trim()
      ) {
        onSaveDraftRef.current?.(
          reponseTextRef.current,
          transferRecipientsRef.current || undefined,
        );
      }
    };
  }, []);

  // Pre-populate fields when mailResponse changes — but not while the user is
  // actively composing, to avoid Convex draft-save responses overwriting input.
  useEffect(() => {
    if (mailResponse && !composeModeRef.current) {
      setReponseText(mailResponse.reponse || "");
      setTransferRecipients(mailResponse.transfertA || "");
      // Initialize ref to avoid triggering auto-save on initial load
      refReponseText.current = mailResponse.reponse || "";
    }
  }, [mailResponse]);

  // Auto-save draft when response text changes significantly
  useEffect(() => {
    // Only auto-save if compose mode is active and there's content
    if (composeMode && reponseText.trim()) {
      const lengthDiff = Math.abs(
        reponseText.length - refReponseText.current.length,
      );

      // Save draft if the text has changed by at least 3 characters
      if (lengthDiff >= 3) {
        onSaveDraft?.(reponseText, transferRecipients || undefined);
        refReponseText.current = reponseText;
      }
    }
  }, [reponseText, composeMode, transferRecipients, onSaveDraft]);

  // Save draft when closing compose mode (if text is not empty and not already sent)
  useEffect(() => {
    // If we had compose mode open and now it's closed
    if (prevComposeModeRef.current && !composeMode) {
      // Skip draft save if the user just clicked "Send"
      if (isSendingRef.current) {
        isSendingRef.current = false;
      } else if (
        reponseText.trim() &&
        mailResponse?.statut !== MailStatus.REPONDU
      ) {
        // Save draft if there's content and status is not already "repondu"
        onSaveDraft?.(reponseText, transferRecipients || undefined);
      } else if (
        !reponseText.trim() &&
        mailResponse?.statut !== MailStatus.REPONDU
      ) {
        // Cancel draft if textarea is empty
        onCancelDraft?.();
      }
    }
    prevComposeModeRef.current = composeMode;
  }, [
    composeMode,
    reponseText,
    transferRecipients,
    mailResponse?.statut,
    onSaveDraft,
    onCancelDraft,
  ]);

  const handleSend = () => {
    if (!onSaveResponse) return;

    isSendingRef.current = true;

    if (composeMode === "forward") {
      onSaveResponse(reponseText, transferRecipients || undefined);
    } else {
      onSaveResponse(reponseText);
    }

    setComposeMode(null);
  };

  const dateText = calculateDateWithDateRelative(
    mailMetadata.dateRelative,
    todayDate,
  );
  const hasAttachments =
    mailMetadata.piecesJointes && mailMetadata.piecesJointes.length > 0;
  const isUnread = !mailResponse || mailResponse.statut === MailStatus.NON_LU;
  const currentColor = mailResponse?.couleur || "bg-white";

  const handleStartEdit = () => {
    setOriginalResponse(reponseText);
    setOriginalTransferRecipients(transferRecipients);
    setComposeMode("reply");
    onStartEdit?.();
  };

  const handleCancelEdit = () => {
    isSendingRef.current = true;
    onSaveResponse?.(originalResponse!, originalTransferRecipients || undefined);
    setReponseText(originalResponse!);
    setTransferRecipients(originalTransferRecipients);
    setOriginalResponse(null);
    setOriginalTransferRecipients("");
    setComposeMode(null);
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-background">
      {/* Header with actions */}
      <header className="shrink-0 border-b bg-card px-4 py-3">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="lg:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold text-foreground truncate max-w-75">
              {mailMetadata.titre}
            </h2>
            {/* Minimal color selector */}
            <Select
              value={currentColor}
              onValueChange={(value) => onColorChange?.(value)}
            >
              <SelectTrigger
                className="h-7 w-12 border-0 p-0 shadow-none focus:ring-0"
                title="Catégorie de couleur"
              >
                <span
                  className={cn(
                    "h-5 w-5 rounded-full border-2 transition-transform hover:scale-110",
                    currentColor === "bg-white"
                      ? "border-gray-300 bg-white"
                      : cn(currentColor, "border-transparent"),
                  )}
                />
              </SelectTrigger>
              <SelectContent align="start">
                {AVAILABLE_COLORS.map((color) => (
                  <SelectItem key={color} value={color}>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-4 w-4 rounded-full border",
                          color === "bg-white"
                            ? "border-gray-300 bg-white"
                            : color,
                        )}
                      />
                      <span className="capitalize">
                        {COLORS_MAPPING[color]}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleImportant}
              title="Marquer comme important"
            >
              <Star
                className={cn(
                  "h-4 w-4",
                  (mailResponse?.important || isPresidentMail) &&
                    "fill-yellow-400 text-yellow-400",
                )}
              />
            </Button>
          </div>
        </div>
      </header>

      {/* Completed banner */}
      {isCompleted && (
        <div className="shrink-0 flex items-center gap-2 border-b bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>
            Évaluation terminée — les réponses ne peuvent plus être modifiées.
          </span>
        </div>
      )}

      {/* Mail content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {/* Sender info */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Avatar className="h-12 w-12" style={{ backgroundColor: getSenderColor(mailMetadata.emetteur) }}>
                <AvatarFallback className="text-white" style={{ backgroundColor: getSenderColor(mailMetadata.emetteur) }}>
                  {getInitials(mailMetadata.emetteur)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {mailMetadata.emetteur}
                  </span>
                  {isUnread && (
                    <Badge variant="default" className="text-xs">
                      Non lu
                    </Badge>
                  )}
                </div>
                {mailMetadata.emetteurPoste && (
                  <p className="text-sm text-muted-foreground">
                    {mailMetadata.emetteurPoste}
                  </p>
                )}
                {mailMetadata.emetteurEntreprise && (
                  <p className="text-sm text-muted-foreground">
                    {mailMetadata.emetteurEntreprise}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">À : moi</p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{dateText}</span>
              </div>
              {hasAttachments && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Paperclip className="h-3 w-3" />
                  <span>
                    {mailMetadata.piecesJointes.length} pièce(s) jointe(s)
                  </span>
                </div>
              )}
            </div>
          </div>

          <Separator className="my-6" />

          {/* Mail body */}
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {CurrentMailComponent && (
              <CurrentMailComponent variablesData={variablesData} />
            )}
          </div>

          {/* Attachments section */}
          {hasAttachments && (
            <>
              <Separator className="my-6" />
              <div>
                <p className="mb-3 text-sm font-medium text-muted-foreground">
                  Pièces jointes ({mailMetadata.piecesJointes.length})
                </p>
                <div className="space-y-2">
                  {mailMetadata.piecesJointes.map((attachmentKey, index) => {
                    const pieceJointe =
                      getPieceJointeContentComponent(attachmentKey);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          if (pieceJointe) {
                            setSelectedAttachment({
                              cle: attachmentKey,
                              titre: pieceJointe.title,
                            });
                          }
                        }}
                        className="flex w-full items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                      >
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {pieceJointe?.title || attachmentKey}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* No response notice / Reply action buttons */}
          {typeReponse === "no_response" ? (
            <>
              <Separator className="my-6" />
              <div className="rounded-lg border border-sidebar-border bg-sidebar px-4 py-3 text-sm text-sidebar-foreground">
                La réponse à ce mail sera intégrée à une autre question.
              </div>
            </>
          ) : null}
          {typeReponse !== "no_response" && mailResponse?.statut !== MailStatus.REPONDU && (
            <>
              <Separator className="my-6" />
              {composeMode ? (
                <div
                  className={cn(
                    "rounded-lg border p-4",
                    composeMode === "forward"
                      ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                      : "bg-card border-border",
                  )}
                >
                  <div className="space-y-3">
                    {/* Header with mode indicator */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {composeMode === "forward" ? (
                          <>
                            <Forward className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              Transférer ce message
                            </span>
                            <Badge variant="info" className="text-xs">
                              Transfert
                            </Badge>
                          </>
                        ) : typeReponse === "list" ? (
                          <>
                            <span className="text-sm font-medium text-muted-foreground">
                              Expliquer mes choix
                            </span>
                            <InfoTooltip />
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium text-muted-foreground">
                              Répondre à {mailMetadata.emetteur}
                            </span>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => originalResponse !== null ? handleCancelEdit() : setComposeMode(null)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Recipient field for forward */}
                    {composeMode === "forward" && (
                      <div className="flex items-center gap-2 rounded-md border border-blue-200 dark:border-blue-800 bg-white dark:bg-background px-3 py-2">
                        <span className="text-sm text-muted-foreground">À :</span>
                        <input
                          type="text"
                          value={transferRecipients}
                          onChange={(e) => setTransferRecipients(e.target.value)}
                          placeholder="destinataire1, destinataire2, ..."
                          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                    )}

                    <Textarea
                      value={reponseText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setReponseText(e.target.value)
                      }
                      placeholder={
                        composeMode === "forward"
                          ? "Ajouter un message (optionnel)..."
                          : typeReponse === "list"
                            ? "Listez les tâches à effectuer..."
                            : "Votre réponse..."
                      }
                      className={cn(
                        "min-h-25 resize-none",
                        composeMode === "forward" &&
                          "border-blue-200 dark:border-blue-800 focus-visible:ring-blue-500",
                      )}
                    />
                    <div className="flex items-center justify-end">
                      <Button
                        size="sm"
                        onClick={handleSend}
                        disabled={composeMode === "reply" && !reponseText.trim()}
                        className={cn(
                          composeMode === "forward" &&
                            "bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700",
                        )}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {composeMode === "forward" ? "Transférer" : "Envoyer"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {typeReponse === "mail" && (
                    <Button
                      variant="default"
                      size="sm"
                      disabled={isCompleted}
                      onClick={() => {
                        setComposeMode("reply");
                        onStartDraft?.();
                      }}
                    >
                      Répondre
                    </Button>
                  )}
                  {typeReponse === "list" && (
                    <Button
                      variant="default"
                      size="sm"
                      disabled={isCompleted}
                      onClick={() => {
                        setComposeMode("reply");
                        onStartDraft?.();
                      }}
                    >
                      Expliquer mes choix
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Saved response section */}
          {mailResponse?.reponse &&
            mailResponse.statut === MailStatus.REPONDU && (
              <>
                <Separator className="my-6" />
                {composeMode ? (
                  <div className="rounded-lg border bg-card border-border p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          {typeReponse === "list"
                            ? "Modifier mes choix"
                            : `Modifier ma réponse à ${mailMetadata.emetteur}`}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={handleCancelEdit}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <Textarea
                        value={reponseText}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setReponseText(e.target.value)
                        }
                        placeholder={
                          typeReponse === "list"
                            ? "Listez les tâches à effectuer..."
                            : "Votre réponse..."
                        }
                        className="min-h-25 resize-none"
                      />
                      <div className="flex items-center justify-end">
                        <Button
                          size="sm"
                          onClick={handleSend}
                          disabled={!reponseText.trim()}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Envoyer
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border-l-4 border-l-green-500 bg-green-50 dark:bg-green-950/30 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Reply className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-600 dark:text-green-400">
                          {typeReponse === "list" ? "Vos choix" : "Votre réponse"}
                        </span>
                        {mailResponse.transfertA && (
                          <Badge variant="outline" className="text-xs">
                            Transféré à : {mailResponse.transfertA}
                          </Badge>
                        )}
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-foreground">
                        {mailResponse.reponse}
                      </div>
                    </div>
                    {!isCompleted && (
                      <div className="mt-3">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleStartEdit}
                        >
                          Modifier ma réponse
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

          {/* Draft indicator */}
          {mailResponse?.estEnBrouillon &&
            mailResponse?.reponse &&
            composeMode === null &&
            mailResponse.statut !== MailStatus.REPONDU && (
              <>
                <Separator className="my-6" />
                <div className="rounded-lg border-l-4 border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Reply className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                      Brouillon
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {mailResponse.reponse}
                  </div>
                </div>
              </>
            )}
        </div>
      </ScrollArea>


      {/* Attachment Modal - rendered via portal */}
      {selectedAttachment &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={() => setSelectedAttachment(null)}
          >
            <div
              className="relative max-h-[80vh] w-full max-w-3xl mx-4 overflow-auto rounded-lg bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedAttachment(null)}
                className="absolute right-4 top-4 rounded-lg p-2 hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
              <h2 className="mb-4 text-xl font-bold text-gray-900">
                {selectedAttachment.titre}
              </h2>
              <div className="prose prose-sm max-w-none text-gray-700">
                {(() => {
                  const ContentComponent = getPieceJointeContentComponent(
                    selectedAttachment.cle,
                  );
                  if (ContentComponent) {
                    return <ContentComponent variablesData={variablesData} />;
                  }
                  return (
                    <p className="text-muted-foreground italic">
                      Contenu de la pièce jointe non disponible
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
